import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useEventStream } from '../../hooks/useEventStream.js';
import type { SessionRow } from '../../lib/api.js';

const STAGE_COLORS: Record<string, string> = {
  scrape:       '#06b6d4',
  parse:        '#ffd166',
  canonicalize: '#0dcfb8',
  export:       '#01b574',
  verify:       '#a855f7',
};

const STALE_MS = 60_000;

interface DotState {
  sessionId: string;
  title: string;
  stage: string;
  startedAt: string | null;
  lastEventAt: number;
  done: boolean;
}

export function HeartbeatStrip({ sessions }: { sessions: SessionRow[] }) {
  const navigate = useNavigate();
  const running = sessions.filter((s) => s.status === 'running');

  const [dots, setDots] = useState<Map<string, DotState>>(() => {
    const m = new Map<string, DotState>();
    for (const s of running) {
      m.set(s.id, {
        sessionId: s.id,
        title: s.title ?? s.id.slice(0, 8),
        stage: 'scrape',
        startedAt: s.startedAt,
        lastEventAt: Date.now(),
        done: false,
      });
    }
    return m;
  });

  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 5_000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    setDots((prev) => {
      const next = new Map(prev);
      for (const s of running) {
        if (!next.has(s.id)) {
          next.set(s.id, {
            sessionId: s.id,
            title: s.title ?? s.id.slice(0, 8),
            stage: 'scrape',
            startedAt: s.startedAt,
            lastEventAt: Date.now(),
            done: false,
          });
        }
      }
      return next;
    });
  }, [sessions]);

  const handleEvent = useCallback((msg: MessageEvent) => {
    try {
      const event = JSON.parse(msg.data as string) as {
        type: string;
        payload: Record<string, unknown>;
      };
      const p = event.payload as { sessionId?: string; stage?: string; status?: string };
      if (!p.sessionId) return;

      setDots((prev) => {
        const next = new Map(prev);
        const dot = next.get(p.sessionId!);
        if (!dot) return prev;

        const updated = { ...dot, lastEventAt: Date.now() };
        if (event.type === 'stage.started' && p.stage) updated.stage = p.stage;
        if (event.type === 'stage.finished' && p.stage === 'export' && p.status === 'done') updated.done = true;
        next.set(p.sessionId!, updated);
        return next;
      });
    } catch { /* ignore */ }
  }, []);

  useEventStream({ topics: ['session:all'], onEvent: handleEvent });

  if (running.length === 0) {
    return (
      <div className="flex items-center gap-2 px-1 py-2 text-xs" style={{ color: 'var(--text-dimmed)' }}>
        <span className="h-2 w-2 rounded-full" style={{ background: 'var(--text-dimmed)' }} />
        No active sessions
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-3 py-2 px-1">
      {Array.from(dots.values()).map((dot) => {
        const stale = now - dot.lastEventAt > STALE_MS;
        const color = dot.done ? 'var(--text-dimmed)' : stale ? 'var(--accent-yellow)' : (STAGE_COLORS[dot.stage] ?? '#0dcfb8');
        const elapsed = dot.startedAt
          ? Math.round((now - new Date(dot.startedAt).getTime()) / 60000)
          : null;

        return (
          <button
            key={dot.sessionId}
            type="button"
            title={`${dot.title} · ${dot.stage}${elapsed !== null ? ` · ${elapsed}m` : ''}`}
            onClick={() => void navigate({ to: '/sessions/$id', params: { id: dot.sessionId } })}
            className="flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-semibold transition-opacity hover:opacity-80"
            style={{
              background: `${color}18`,
              border: `1px solid ${color}55`,
              color,
            }}
          >
            <span
              className={`h-2 w-2 rounded-full shrink-0 ${!dot.done && !stale ? 'pulse-dot' : ''}`}
              style={{ background: color, color }}
            />
            <span className="max-w-[120px] truncate">{dot.title}</span>
            <span className="opacity-60 font-mono">{dot.stage.slice(0, 3)}</span>
          </button>
        );
      })}
    </div>
  );
}
